import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ReferralShareKit } from '../components/ReferralShareKit';
import { AffiliateBannerAdmin } from '../components/AffiliateBannerAdmin';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  Handshake, Copy, Trophy, Users, DollarSign, Clock, Check, X, Loader2,
  Medal, Crown, Wand2, ShieldCheck, TrendingUp, CreditCard, CheckCircle2, FileText, Circle, MousePointerClick, Percent,
} from 'lucide-react';
import { toast } from 'sonner';

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const TIER_STYLE = {
  platinum: 'bg-cyan-100 text-cyan-700', gold: 'bg-amber-100 text-amber-700',
  silver: 'bg-zinc-200 text-zinc-700', bronze: 'bg-orange-100 text-orange-700',
  starter: 'bg-zinc-100 text-zinc-500',
};

export const AffiliatePage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);      // /affiliate/me
  const [board, setBoard] = useState([]);
  const [applying, setApplying] = useState(false);
  const [form, setForm] = useState({ promo_method: '', audience: '', website: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [me, lb] = await Promise.all([api.get('/affiliate/me'), api.get('/affiliate/leaderboard')]);
      setData(me.data);
      setBoard(lb.data.leaderboard || []);
    } catch (_) { /* surfaced below */ } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const apply = async () => {
    setApplying(true);
    try {
      await api.post('/affiliate/apply', form);
      toast.success('Application submitted — an admin will review it shortly');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not apply'); }
    finally { setApplying(false); }
  };

  const [connecting, setConnecting] = useState(false);
  const startPayoutSetup = async () => {
    setConnecting(true);
    try {
      const r = await api.post('/affiliate/connect/onboard');
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start payout setup'); setConnecting(false); }
  };

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('connect')) {
      api.get('/affiliate/connect/status').then(() => load()).catch(() => {});
      if (p.get('connect') === 'done') toast.success('Refreshing your payout setup…');
      window.history.replaceState({}, '', '/affiliate');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementText, setAgreementText] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const openAgreement = async () => {
    setAgreed(false);
    try {
      const r = await api.get('/affiliate/agreement');
      setAgreementText(r.data.text || '');
      setAgreementOpen(true);
    } catch (e) { toast.error('Could not load the agreement'); }
  };
  const acceptAgreement = async () => {
    setAccepting(true);
    try {
      await api.post('/affiliate/accept-agreement');
      toast.success('Partner Agreement accepted');
      setAgreementOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not accept'); }
    finally { setAccepting(false); }
  };


  const aff = data?.affiliate;
  const settings = data?.settings || {};
  const onboarding = data?.onboarding;
  const agreementDone = onboarding?.steps?.find((s) => s.key === 'agreement')?.done;
  const ytdCents = data?.ytd_earnings_cents || 0;
  const taxFlag = data?.tax_1099_flag;
  const refLink = aff ? `${window.location.origin}/?ref=${aff.code}` : '';
  const copy = (txt, msg) => { navigator.clipboard.writeText(txt); toast.success(msg || 'Copied'); };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="affiliate-page">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center">
            <Handshake className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Partner Program</h1>
            <p className="text-sm text-zinc-500">Refer PostToSocial and earn recurring commission on every payment.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
        ) : (
          <>
            {/* ---- Apply / status / dashboard ---- */}
            {!aff && (
              <Card data-testid="affiliate-apply-card">
                <CardHeader><CardTitle className="text-lg">Become a partner</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Stat icon={DollarSign} label="Commission" value={`${settings.commission_pct ?? 30}%`} sub="of every payment" />
                    <Stat icon={TrendingUp} label="Recurring" value="Monthly" sub="as long as they stay" />
                    <Stat icon={Trophy} label="Leaderboard" value="Tiers & badges" sub="climb the ranks" />
                  </div>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">How will you promote us?</Label>
                      <Input value={form.promo_method} onChange={(e) => setForm({ ...form, promo_method: e.target.value })} placeholder="e.g. YouTube reviews, newsletter, agency clients" data-testid="apply-promo" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div><Label className="text-xs">Audience</Label><Input value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} placeholder="Who is your audience?" data-testid="apply-audience" /></div>
                      <div><Label className="text-xs">Website / channel</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" data-testid="apply-website" /></div>
                    </div>
                  </div>
                  <Button onClick={apply} disabled={applying} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="apply-submit">
                    {applying ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Handshake className="w-4 h-4 mr-1.5" />} Apply to join
                  </Button>
                </CardContent>
              </Card>
            )}

            {aff && aff.status === 'pending' && (
              <Card data-testid="affiliate-pending"><CardContent className="py-6 flex items-center gap-3 text-amber-700">
                <Clock className="w-5 h-5" /> Your application is under review. You'll be notified once approved.
              </CardContent></Card>
            )}
            {aff && aff.status === 'rejected' && (
              <Card><CardContent className="py-6 flex items-center gap-3 text-rose-700"><X className="w-5 h-5" /> Your application was not approved.</CardContent></Card>
            )}

            {aff && aff.status === 'approved' && (
              <>
                {onboarding && !onboarding.complete && (
                  <Card className="border-indigo-200 bg-indigo-50/30" data-testid="onboarding-checklist">
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><Handshake className="w-4 h-4 text-indigo-600" /> Finish setting up your partner account</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {onboarding.steps.map((s) => (
                        <div key={s.key} className="flex items-center justify-between gap-3" data-testid={`onboard-step-${s.key}`}>
                          <div className="flex items-center gap-2.5">
                            {s.done ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-zinc-300" />}
                            <span className={`text-sm ${s.done ? 'text-zinc-500 line-through' : 'text-zinc-800 font-medium'}`}>{s.label}</span>
                          </div>
                          {!s.done && onboarding.current_step === s.key && s.key === 'agreement' && (
                            <Button size="sm" onClick={openAgreement} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="review-agreement-btn"><FileText className="w-3.5 h-3.5 mr-1.5" /> Review &amp; accept</Button>
                          )}
                          {!s.done && onboarding.current_step === s.key && s.key === 'payouts' && (
                            <Button size="sm" onClick={startPayoutSetup} disabled={connecting} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="onboard-setup-payouts-btn">{connecting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <CreditCard className="w-3.5 h-3.5 mr-1.5" />} Set up</Button>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="affiliate-stats">
                  <Stat icon={MousePointerClick} label="Link clicks" value={data?.clicks || 0} />
                  <Stat icon={Users} label="Referred" value={aff.referred_users || 0} />
                  <Stat icon={Percent} label="Conversion" value={`${data?.conversion_rate || 0}%`} />
                  <Stat icon={Check} label="Paying" value={aff.paying_customers || 0} />
                  <Stat icon={DollarSign} label="Unpaid balance" value={money(aff.unpaid_commission_cents)} />
                  <Stat icon={DollarSign} label="Paid out" value={money(aff.paid_commission_cents)} />
                </div>

                <Card data-testid="affiliate-link-card">
                  <CardHeader><CardTitle className="text-base flex items-center gap-2">Your referral link
                    <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${TIER_STYLE[aff.tier?.key] || TIER_STYLE.starter}`}>{aff.tier?.label} tier</span>
                  </CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-2">
                      <Input readOnly value={refLink} className="text-xs font-mono" data-testid="affiliate-link" />
                      <Button variant="outline" onClick={() => copy(refLink, 'Referral link copied')} data-testid="copy-link"><Copy className="w-4 h-4" /></Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500">Or share code:</span>
                      <code className="text-sm font-bold text-indigo-700" data-testid="affiliate-code">{aff.code}</code>
                      <Button size="sm" variant="ghost" onClick={() => copy(aff.code, 'Code copied')}><Copy className="w-3.5 h-3.5" /></Button>
                    </div>
                    <p className="text-[11px] text-zinc-400">You earn {settings.commission_pct}% of every payment made by users who sign up through your link/code. Minimum payout {money(settings.min_payout_cents)}.</p>
                  </CardContent>
                </Card>

                <ReferralShareKit link={refLink} code={aff.code} />

                <Card data-testid="payout-setup-card">
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><CreditCard className="w-4 h-4 text-indigo-600" /> Payouts</CardTitle></CardHeader>
                  <CardContent>
                    {aff.payouts_enabled ? (
                      <div className="flex items-center gap-2 text-emerald-700 text-sm" data-testid="payouts-enabled">
                        <CheckCircle2 className="w-4 h-4" /> Stripe payouts are set up. Your commission balance is sent to your connected account.
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        <p className="text-sm text-zinc-500">Connect a Stripe account to receive your commission payouts. Stripe handles onboarding &amp; your bank details securely.</p>
                        {!agreementDone ? (
                          <p className="text-[12px] text-amber-700 flex items-center gap-1.5" data-testid="payout-blocked-hint"><FileText className="w-3.5 h-3.5" /> Accept the Partner Agreement above before setting up payouts.</p>
                        ) : (
                          <Button onClick={startPayoutSetup} disabled={connecting} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="setup-payouts-btn">
                            {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CreditCard className="w-4 h-4 mr-1.5" />}
                            {aff.stripe_connect_account_id ? 'Finish payout setup' : 'Set up payouts with Stripe'}
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card data-testid="tax-info-card">
                  <CardContent className="py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div>
                      <p className="text-xs text-zinc-400">Earnings this year (for tax reporting)</p>
                      <p className="text-lg font-bold text-zinc-900" data-testid="ytd-earnings">{money(ytdCents)}</p>
                    </div>
                    {taxFlag ? (
                      <span className="text-[11px] px-2 py-1 rounded-md bg-amber-100 text-amber-700 flex items-center gap-1" data-testid="tax-flag"><FileText className="w-3.5 h-3.5" /> A 1099 tax form will be issued for your earnings</span>
                    ) : (
                      <span className="text-[11px] text-zinc-400 sm:max-w-[280px] sm:text-right">Your tax details are collected securely by Stripe — we never store your SSN/EIN. A 1099 is issued if your yearly earnings reach the reporting threshold.</span>
                    )}
                  </CardContent>
                </Card>


                <Card>
                  <CardHeader><CardTitle className="text-base">Recent commissions</CardTitle></CardHeader>
                  <CardContent>
                    {(data.commissions || []).length === 0 ? (
                      <p className="text-sm text-zinc-400">No commissions yet — share your link to start earning.</p>
                    ) : (
                      <div className="divide-y" data-testid="commissions-list">
                        {data.commissions.slice(0, 12).map((c) => (
                          <div key={c.id} className="flex items-center justify-between py-2 text-sm">
                            <span className="text-zinc-500">{new Date(c.created_at).toLocaleDateString()} · {c.rate_pct}% of {money(c.revenue_cents)}</span>
                            <span className="flex items-center gap-2 font-medium text-emerald-600">{money(c.amount_cents)}
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.payout_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>{c.payout_status}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {/* ---- Leaderboard ---- */}
            <Card data-testid="leaderboard-card">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Top partners</CardTitle></CardHeader>
              <CardContent>
                {board.length === 0 ? <p className="text-sm text-zinc-400">No partners on the board yet — be the first!</p> : (
                  <div className="divide-y">
                    {board.map((row) => (
                      <div key={row.id} className="flex items-center gap-3 py-2.5" data-testid={`leaderboard-row-${row.rank}`}>
                        <span className="w-6 text-center font-bold text-zinc-400">
                          {row.rank === 1 ? <Crown className="w-5 h-5 text-amber-500 mx-auto" /> : row.rank <= 3 ? <Medal className="w-5 h-5 text-zinc-400 mx-auto" /> : row.rank}
                        </span>
                        <span className="flex-1 font-medium text-zinc-800">{row.display_name}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${TIER_STYLE[row.tier?.key] || TIER_STYLE.starter}`}>{row.tier?.label}</span>
                        <span className="text-xs text-zinc-500 w-20 text-right">{row.paying_customers} paying</span>
                        <span className="text-sm font-semibold text-zinc-800 w-24 text-right">{money(row.total_revenue_cents)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {user?.is_admin && <AdminPanel api={api} onChange={load} />}
            {user?.is_admin && <AffiliateBannerAdmin api={api} />}
          </>
        )}
      </div>

      <Dialog open={agreementOpen} onOpenChange={setAgreementOpen}>
        <DialogContent className="max-w-2xl" data-testid="agreement-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-600" /> Partner Agreement</DialogTitle>
            <DialogDescription>Please read and accept the agreement to continue setting up payouts.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[45vh] overflow-y-auto rounded-md border border-zinc-200 bg-zinc-50 p-4 text-[13px] text-zinc-700 whitespace-pre-wrap" data-testid="agreement-text">
            {agreementText}
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
            <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} data-testid="agreement-checkbox" />
            I have read and agree to the Partner Agreement.
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAgreementOpen(false)}>Cancel</Button>
            <Button onClick={acceptAgreement} disabled={!agreed || accepting} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="accept-agreement-btn">
              {accepting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Check className="w-4 h-4 mr-1.5" />} Accept &amp; continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

const Stat = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-lg border border-zinc-200 p-4">
    <div className="flex items-center gap-1.5 text-zinc-400 text-xs"><Icon className="w-3.5 h-3.5" /> {label}</div>
    <div className="text-xl font-bold text-zinc-900 mt-1">{value}</div>
    {sub && <div className="text-[11px] text-zinc-400">{sub}</div>}
  </div>
);

const AdminPanel = ({ api, onChange }) => {
  const [list, setList] = useState([]);
  const [settings, setSettings] = useState({ commission_pct: 30, cookie_days: 60, min_payout_cents: 5000 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([api.get('/affiliate/admin/list'), api.get('/affiliate/admin/settings')]);
      setList(l.data.affiliates || []);
      setSettings(s.data);
    } catch (_) { /* noop */ }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/affiliate/admin/settings', {
        commission_pct: Number(settings.commission_pct), cookie_days: Number(settings.cookie_days), min_payout_cents: Number(settings.min_payout_cents),
        tax_1099_threshold_cents: Number(settings.tax_1099_threshold_cents ?? 200000),
        auto_payout_enabled: !!settings.auto_payout_enabled,
        auto_payout_frequency: settings.auto_payout_frequency || 'monthly',
        auto_payout_day: Number(settings.auto_payout_day ?? 1),
      });
      toast.success('Settings saved'); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); } finally { setSaving(false); }
  };

  const [running, setRunning] = useState(false);
  const runPayoutsNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('/affiliate/admin/run-payouts');
      const d = r.data;
      if (d.paid_count === 0 && d.failed_count === 0) toast.info('No partners were eligible for payout right now');
      else toast.success(`Paid ${d.paid_count} partner(s) — ${money(d.total_cents)} total${d.failed_count ? ` · ${d.failed_count} failed` : ''}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Batch payout failed'); }
    finally { setRunning(false); }
  };

  const act = async (id, action) => {
    try { await api.post(`/affiliate/admin/${id}/${action}`); toast.success(`Affiliate ${action}d`); load(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const payout = async (a) => {
    const amt = a.unpaid_commission_cents || 0;
    if (amt <= 0) { toast.error('No unpaid balance'); return; }
    try { await api.post(`/affiliate/admin/${a.id}/payout`, { amount_cents: amt, note: 'Manual payout' }); toast.success(`Marked ${money(amt)} paid`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Payout failed'); }
  };

  const payoutStripe = async (a) => {
    if ((a.unpaid_commission_cents || 0) <= 0) { toast.error('No unpaid balance'); return; }
    try { const r = await api.post(`/affiliate/admin/${a.id}/payout-stripe`); toast.success(`Paid ${money(r.data.amount_cents)} via Stripe`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Stripe payout failed'); }
  };

  return (
    <Card className="border-indigo-200" data-testid="affiliate-admin-panel">
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-indigo-600" /> Admin — Partner Program</CardTitle></CardHeader>
      <CardContent className="space-y-5">
        <div className="grid sm:grid-cols-5 gap-3 items-end">
          <div><Label className="text-xs">Commission %</Label><Input type="number" value={settings.commission_pct} onChange={(e) => setSettings({ ...settings, commission_pct: e.target.value })} data-testid="admin-commission-pct" /></div>
          <div><Label className="text-xs">Cookie days</Label><Input type="number" value={settings.cookie_days} onChange={(e) => setSettings({ ...settings, cookie_days: e.target.value })} /></div>
          <div><Label className="text-xs">Min payout ($)</Label><Input type="number" value={(settings.min_payout_cents || 0) / 100} onChange={(e) => setSettings({ ...settings, min_payout_cents: Math.round(Number(e.target.value) * 100) })} /></div>
          <div><Label className="text-xs">1099 threshold ($)</Label><Input type="number" value={(settings.tax_1099_threshold_cents ?? 200000) / 100} onChange={(e) => setSettings({ ...settings, tax_1099_threshold_cents: Math.round(Number(e.target.value) * 100) })} data-testid="admin-1099-threshold" /></div>
          <Button onClick={saveSettings} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="admin-save-settings">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
        </div>

        <div className="rounded-lg border border-zinc-200 p-3.5 space-y-3" data-testid="auto-payout-settings">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-zinc-800 flex items-center gap-1.5"><Clock className="w-4 h-4 text-indigo-600" /> Automatic payouts</p>
              <p className="text-[11px] text-zinc-400">Pay every partner with Stripe set up and a balance ≥ the min payout, on a schedule — no clicks needed.</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-zinc-600 shrink-0">
              <Switch checked={!!settings.auto_payout_enabled} onCheckedChange={(v) => setSettings({ ...settings, auto_payout_enabled: v })} data-testid="auto-payout-toggle" />
              {settings.auto_payout_enabled ? 'On' : 'Off'}
            </label>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs">Frequency</Label>
              <Select value={settings.auto_payout_frequency || 'monthly'} onValueChange={(v) => setSettings({ ...settings, auto_payout_frequency: v })}>
                <SelectTrigger data-testid="auto-payout-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {settings.auto_payout_frequency === 'weekly' && (
              <div>
                <Label className="text-xs">Day of week</Label>
                <Select value={String(settings.auto_payout_day ?? 1)} onValueChange={(v) => setSettings({ ...settings, auto_payout_day: Number(v) })}>
                  <SelectTrigger data-testid="auto-payout-weekday"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {settings.auto_payout_frequency === 'monthly' && (
              <div><Label className="text-xs">Day of month (1-28)</Label><Input type="number" min="1" max="28" value={settings.auto_payout_day ?? 1} onChange={(e) => setSettings({ ...settings, auto_payout_day: Number(e.target.value) })} data-testid="auto-payout-monthday" /></div>
            )}
            <Button variant="outline" onClick={runPayoutsNow} disabled={running} data-testid="run-payouts-now" className="col-start-1 sm:col-auto">{running ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <DollarSign className="w-4 h-4 mr-1.5" />} Run payouts now</Button>
          </div>
          {settings.last_auto_payout_at && <p className="text-[11px] text-zinc-400" data-testid="last-auto-payout">Last automatic run: {new Date(settings.last_auto_payout_at).toLocaleString()}</p>}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-zinc-600">Applications & partners</p>
            {list.filter((a) => a.status === 'pending').length > 0 && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium" data-testid="pending-count">
                {list.filter((a) => a.status === 'pending').length} pending approval
              </span>
            )}
          </div>
          {list.length === 0 ? <p className="text-sm text-zinc-400">No applications yet.</p> : list.map((a) => (
            <div key={a.id} className={`flex items-center gap-2 border rounded-md p-2.5 text-sm ${a.status === 'pending' ? 'border-amber-300 bg-amber-50/50' : 'border-zinc-200'}`} data-testid={`admin-affiliate-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{a.user_name || a.user_email} <code className="text-[11px] text-indigo-600">{a.code}</code></div>
                <div className="text-[11px] text-zinc-400">{a.paying_customers} paying · {money(a.total_revenue_cents)} rev · unpaid {money(a.unpaid_commission_cents)} · YTD {money(a.ytd_earnings_cents)}</div>
              </div>
              {a.tax_1099_flag && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1" data-testid={`tax-flag-${a.id}`} title="Year-to-date earnings crossed the 1099 reporting threshold"><FileText className="w-3 h-3" /> 1099</span>}
              {a.status === 'approved' && (a.payouts_enabled
                ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid={`payouts-ready-${a.id}`}>payouts ready</span>
                : <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500" data-testid={`payouts-pending-${a.id}`}>payouts pending</span>)}
              <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${a.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : a.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-500'}`}>{a.status}</span>
              {a.status === 'pending' && <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act(a.id, 'approve')} data-testid={`approve-${a.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => act(a.id, 'reject')} data-testid={`reject-${a.id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
              </>}
              {a.status === 'approved' && a.payouts_enabled && <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => payoutStripe(a)} data-testid={`payout-stripe-${a.id}`}>Pay via Stripe</Button>}
              {a.status === 'approved' && <Button size="sm" variant="outline" onClick={() => payout(a)} data-testid={`payout-${a.id}`}>Mark paid</Button>}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-zinc-400">Partners with completed Stripe onboarding show a "Pay via Stripe" button (transfers their unpaid balance). Tax IDs are collected & 1099s are filed by Stripe — we never store SSN/EIN. "Mark paid" records an off-platform payout.</p>
      </CardContent>
    </Card>
  );
};

export default AffiliatePage;
